/**
 * F17 T17.1.5 — Generic RadioGroup primitive.
 *
 * Pierwszy reusable RadioGroup w admin SPA (memory: 2026-05-25 — RadioGroup
 * nie istniał, plan T17.1.5 wymaga dla ApproverPicker UI wariantu C — radio
 * buttons z labelami i opisami per typ akceptanta).
 *
 * Każda opcja może mieć opcjonalny `description` (pomocniczy tekst pod
 * labelem) + opcjonalny `disabled` (z tooltipem przyczyny).
 *
 * Accessibility:
 * - Każdy radio input ma unikalne id (z `name` + opcji value)
 * - `<fieldset>` + `<legend>` dla semantyki grupy
 * - Klawiatura: standard radio nav (arrow keys w grupie, Tab in/out)
 */

import { useId } from 'react';

export interface RadioOption<T extends string = string> {
  value: T;
  label: string;
  description?: string;
  disabled?: boolean;
  disabledReason?: string;
}

export interface RadioGroupProps<T extends string = string> {
  /** Wartość aktualnie wybrana (controlled). */
  value: T | null;
  /** Callback przy zmianie wyboru. */
  onChange: (value: T) => void;
  /** Opcje radio buttons. */
  options: ReadonlyArray<RadioOption<T>>;
  /** Etykieta grupy (visible). */
  legend?: string;
  /** `name` HTML atrybut — wymagany żeby radio buttons były grupą. */
  name: string;
  /** Klasy dla wrapper'a. */
  className?: string;
  /** Test-id na wrapperze. */
  'data-testid'?: string;
}

export function RadioGroup<T extends string = string>({
  value,
  onChange,
  options,
  legend,
  name,
  className,
  'data-testid': testId,
}: RadioGroupProps<T>) {
  const idPrefix = useId();

  return (
    <fieldset
      className={`space-y-2 ${className ?? ''}`}
      data-testid={testId ?? `radio-group-${name}`}
    >
      {legend && <legend className="text-sm font-medium text-text-primary">{legend}</legend>}
      {options.map((option) => {
        const inputId = `${idPrefix}-${option.value}`;
        const isSelected = value === option.value;
        return (
          <label
            key={option.value}
            htmlFor={inputId}
            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
              isSelected
                ? 'border-accent-500 bg-accent-50 dark:bg-accent-900/20'
                : 'border-border-primary bg-bg-primary hover:bg-bg-secondary'
            } ${option.disabled ? 'cursor-not-allowed opacity-50' : ''}`}
            title={option.disabled ? option.disabledReason : undefined}
          >
            <span className="relative mt-0.5 inline-flex h-5 w-5 shrink-0">
              <input
                type="radio"
                id={inputId}
                name={name}
                value={option.value}
                checked={isSelected}
                onChange={() => onChange(option.value)}
                disabled={option.disabled}
                className={[
                  'peer h-5 w-5 shrink-0 cursor-pointer appearance-none rounded-full border bg-bg-primary transition-colors',
                  'border-border-secondary hover:border-border-primary checked:border-accent-600',
                  'focus:outline-none focus-visible:ring-[3px] focus-visible:ring-accent-600',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                ].join(' ')}
                data-testid={`radio-option-${option.value}`}
              />
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 m-auto h-2.5 w-2.5 scale-0 rounded-full bg-accent-600 transition-transform peer-checked:scale-100"
              />
            </span>
            <div className="flex-1">
              <div className="text-sm font-medium text-text-primary">{option.label}</div>
              {option.description && (
                <div className="mt-0.5 text-xs text-text-secondary">{option.description}</div>
              )}
              {option.disabled && option.disabledReason && (
                <div
                  className="mt-1 text-xs italic text-amber-600 dark:text-amber-400"
                  data-testid={`radio-option-${option.value}-disabled-reason`}
                >
                  {option.disabledReason}
                </div>
              )}
            </div>
          </label>
        );
      })}
    </fieldset>
  );
}
